import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Play, Pause, Archive, Calendar, Target, Filter, AlertCircle, Loader2, Edit, X, IndianRupee } from 'lucide-react';
import type { Campaign, CampaignStats, CampaignFilters } from '@/types/campaigns';
import { campaignsApi } from '@/api/client';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';
import { campaignBudgetMin, validateCampaignBudget } from '@/utils/campaignCreate';
import { budgetPeriodFromType } from '@/utils/marketplaceBudgetLimits';

const formatCurrency = (amount: number) => {
  return formatAccountCurrency(amount);
};

// Utility function to format dates
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Status badge component with semantic colors and icons
const StatusBadge = ({ state }: { state: Campaign['state'] }) => {
  const statusConfig = {
    ENABLED: {
      icon: Play,
      label: 'ENABLED',
      className: 'bg-success/10 text-success border-success/20',
    },
    PAUSED: {
      icon: Pause,
      label: 'PAUSED',
      className: 'bg-warning/10 text-warning border-warning/20',
    },
    ARCHIVED: {
      icon: Archive,
      label: 'ARCHIVED',
      className: 'bg-archived/10 text-archived border-archived/20',
    },
  };

  const config = statusConfig[state];
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-medium border ${config.className}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </div>
  );
};

// Summary statistics component
const CampaignStatsDisplay = ({ stats }: { stats: CampaignStats }) => {
  const statCards = [
    {
      label: 'Enabled',
      value: stats.enabledCampaigns,
      total: stats.totalCampaigns,
      icon: Play,
      color: 'text-success',
      bgColor: 'bg-success/5',
    },
    {
      label: 'Paused',
      value: stats.pausedCampaigns,
      total: stats.totalCampaigns,
      icon: Pause,
      color: 'text-warning',
      bgColor: 'bg-warning/5',
    },
    {
      label: 'Archived',
      value: stats.archivedCampaigns,
      total: stats.totalCampaigns,
      icon: Archive,
      color: 'text-archived',
      bgColor: 'bg-archived/5',
    },
    {
      label: 'Total Budget',
      value: formatCurrency(stats.totalBudget),
      icon: IndianRupee,
      color: 'text-primary',
      bgColor: 'bg-primary/5',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div key={index} className={`${stat.bgColor} rounded-xl p-6 border border-border/50`}>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{typeof stat.value === 'string' ? stat.value : stat.value}</p>
                  {stat.total && (
                    <p className="text-sm text-muted-foreground">
                      of {stat.total}
                    </p>
                  )}
                </div>
              </div>
              <Icon className={`w-8 h-8 ${stat.color}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Individual campaign card component
const CampaignCard = ({ 
  campaign, 
  onEditBudget,
  onEditHourlyBudget
}: { 
  campaign: Campaign;
  onEditBudget: (campaign: Campaign) => void;
  onEditHourlyBudget: (campaign: Campaign) => void;
}) => {
  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1 flex-1">
          <h3 className="font-semibold text-lg text-card-foreground group-hover:text-primary transition-colors">
            {campaign.name}
          </h3>
          <code className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
            {campaign.campaignId}
          </code>
        </div>
        <StatusBadge state={campaign.state} />
      </div>

      {/* Campaign Details */}
      <div className="space-y-3">
        {/* Budget with Edit Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Budget:</span>
            <span className="font-medium">{formatCurrency(campaign.budget.budget)}</span>
            <span className="text-xs text-muted-foreground">/{campaign.budget.budgetType.toLowerCase()}</span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onEditBudget(campaign)}
              className="text-primary hover:text-primary/80 p-1 rounded-pill hover:bg-primary/10 transition-colors"
              title="Edit Budget"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEditHourlyBudget(campaign)}
              className="text-primary hover:text-primary/80 p-1 rounded-pill hover:bg-primary/10 transition-colors"
              title="Edit Hourly Budget"
            >
              <span className="font-bold text-xs">Hr</span>
            </button>
          </div>
        </div>

        {/* Start Date */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Start Date:</span>
          <span className="font-medium">{formatDate(campaign.startDate)}</span>
        </div>

        {/* Targeting Type */}
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Targeting:</span>
          <span className="font-medium capitalize">{campaign.targetingType.toLowerCase()}</span>
        </div>

        {/* End Date if available */}
        {campaign.endDate && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">End Date:</span>
            <span className="font-medium">{formatDate(campaign.endDate)}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-6 pt-4 border-t border-border">
        <Link
          to={`/campaigns/${campaign.id}`}
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center"
        >
          View Details
        </Link>
        <button className="flex-1 border border-input bg-background hover:bg-muted px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          Edit
        </button>
      </div>
    </div>
  );
};
// Hourly Budget Modal
const HourlyBudgetModal = ({
  campaign,
  isOpen,
  onClose,
  onUpdate
}: {
  campaign: Campaign;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (params: { campaignId: string; hourlyBudget: number; active?: boolean; metadata?: Record<string, any> }) => void;
}) => {
  const [hourlyBudget, setHourlyBudget] = useState('');
  const [active, setActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const budgetValue = parseFloat(hourlyBudget);
    if (isNaN(budgetValue) || budgetValue <= 0) {
      alert('Please enter a valid hourly budget amount');
      return;
    }
    setIsSubmitting(true);
    try {
      await onUpdate({
        campaignId: campaign.id, // Supabase campaign id
        hourlyBudget: budgetValue,
        active,
      });
      onClose();
    } catch (error) {
      alert('Failed to update hourly budget. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Set Hourly Budget</h3>
          <button onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="hourlyBudget" className="block text-sm font-medium text-foreground-muted mb-2">
              Hourly Budget (₹)
            </label>
            <input
              type="number"
              id="hourlyBudget"
              step="0.01"
              min="0"
              value={hourlyBudget}
              onChange={e => setHourlyBudget(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter hourly budget"
              disabled={isSubmitting}
            />
          </div>
          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={active}
              onChange={e => setActive(e.target.checked)}
              disabled={isSubmitting}
            />
            <label htmlFor="active" className="text-sm text-foreground-muted">Active</label>
          </div>
          <p className="mb-4 text-xs text-foreground-subtle">
            Schedule times and multipliers are configured on the campaign details page.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-border text-foreground-muted rounded-lg hover:bg-page transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Search and filter component
const CampaignSearch = ({ 
  filters, 
  onFiltersChange 
}: { 
  filters: CampaignFilters;
  onFiltersChange: (filters: CampaignFilters) => void;
}) => {
  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search campaigns by name..."
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button 
            onClick={() => onFiltersChange({ ...filters, state: filters.state === 'ENABLED' ? undefined : 'ENABLED' })}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.state === 'ENABLED' 
                ? 'bg-success text-success-foreground' 
                : 'border border-input bg-background hover:bg-muted'
            }`}
          >
            <Filter className="w-4 h-4" />
            Enabled
          </button>
          <button 
            onClick={() => onFiltersChange({ ...filters, state: filters.state === 'PAUSED' ? undefined : 'PAUSED' })}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.state === 'PAUSED' 
                ? 'bg-warning text-warning-foreground' 
                : 'border border-input bg-background hover:bg-muted'
            }`}
          >
            <Filter className="w-4 h-4" />
            Paused
          </button>
        </div>
      </div>
    </div>
  );
};

// Loading component
const LoadingState = () => (
  <div className="min-h-screen gradient-subtle">
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Amazon Campaigns</h1>
        <p className="text-muted-foreground">Manage and monitor your advertising campaigns</p>
      </div>
      
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Loading Campaigns</h3>
          <p className="text-muted-foreground">Fetching your Amazon Ads campaigns...</p>
        </div>
      </div>
    </div>
  </div>
);

// Error component
const ErrorState = ({ error, onRetry }: { error: any; onRetry: () => void }) => (
  <div className="min-h-screen gradient-subtle">
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Amazon Campaigns</h1>
        <p className="text-muted-foreground">Manage and monitor your advertising campaigns</p>
      </div>
      
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to Load Campaigns</h3>
          <p className="text-muted-foreground mb-4">
            {error?.response?.data?.detail || error?.message || 'An unexpected error occurred while fetching campaigns.'}
          </p>
          <button 
            onClick={onRetry}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Budget Update Modal Component
const BudgetUpdateModal = ({ 
  campaign, 
  isOpen, 
  onClose, 
  onUpdate 
}: { 
  campaign: Campaign;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (campaignId: string, budget: number) => void;
}) => {
  const [newBudget, setNewBudget] = useState(campaign.budget.budget.toString());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const budgetValue = parseFloat(newBudget);
    
    const budgetError = validateCampaignBudget(
      budgetValue,
      'SP',
      budgetPeriodFromType(campaign.budget.budgetType)
    );
    if (budgetError) {
      alert(budgetError);
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdate(campaign.campaignId, budgetValue);
      onClose();
    } catch (error) {
      console.error('Budget update failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Update Campaign Budget</h3>
          <button 
            onClick={onClose}
            className="text-chrome-label hover:text-foreground-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-foreground-muted mb-2">Campaign: {campaign.name}</p>
          <p className="text-sm text-foreground-muted">
            Current Budget: {formatCurrency(campaign.budget.budget)}/{campaign.budget.budgetType.toLowerCase()}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="budget" className="block text-sm font-medium text-foreground-muted mb-2">
              New Budget Amount (₹)
            </label>
            <input
              type="number"
              id="budget"
              step="0.01"
              min={campaignBudgetMin('SP', budgetPeriodFromType(campaign.budget.budgetType)) ?? undefined}
              value={newBudget}
              onChange={(e) => setNewBudget(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter new budget amount"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-border text-foreground-muted rounded-lg hover:bg-page transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Budget'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main component
export const CampaignsList = () => {
  const [filters, setFilters] = useState<CampaignFilters>({
    search: '',
    state: undefined,
    targetingType: undefined,
  });

  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingHourlyCampaign, setEditingHourlyCampaign] = useState<Campaign | null>(null);

  const queryClient = useQueryClient();

  // Fetch campaigns using React Query
  const { 
    data: campaignsResponse, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['campaigns'],
    queryFn: campaignsApi.getCampaigns,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Budget update mutation
  const budgetUpdateMutation = useMutation({
    mutationFn: ({ campaignId, budget }: { campaignId: string; budget: number }) => 
      campaignsApi.updateCampaignBudget(campaignId, { budget, budgetType: 'DAILY' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      console.log('✅ Budget updated successfully');
    },
    onError: (error) => {
      console.error('❌ Budget update failed:', error);
      alert('Failed to update budget. Please try again.');
    },
  });

  // Hourly budget mutation
  const hourlyBudgetMutation = useMutation({
    mutationFn: async ({
      campaignId,
      hourlyBudget,
      active,
      metadata,
    }: {
      campaignId: string;
      hourlyBudget: number;
      active?: boolean;
      metadata?: Record<string, any>;
    }) => {
      const profileId = (import.meta.env.VITE_LEGACY_CAMPAIGNS_PROFILE_ID as string | undefined)?.trim();
      if (!profileId) {
        alert(
          'Hourly budget from this page requires VITE_LEGACY_CAMPAIGNS_PROFILE_ID, or use Accounts → Campaigns.'
        );
        throw new Error('Missing profile id for hourly budget');
      }
      return campaignsApi.updateCampaignHourlyBudget(profileId, campaignId, {
        hourly_budget: hourlyBudget,
        active,
        metadata,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      console.log('✅ Hourly budget updated successfully');
    },
    onError: (error) => {
      console.error('❌ Hourly budget update failed:', error);
      alert('Failed to update hourly budget. Please try again.');
    },
  });

  const handleBudgetUpdate = async (campaignId: string, budget: number) => {
    await budgetUpdateMutation.mutateAsync({ campaignId, budget });
  };

  const handleHourlyBudgetUpdate = async (params: { campaignId: string; hourlyBudget: number; active?: boolean; metadata?: Record<string, any> }) => {
    await hourlyBudgetMutation.mutateAsync(params);
  };

  // Calculate stats from campaigns data
  const campaignStats: CampaignStats = useMemo(() => {
    const campaigns = campaignsResponse?.data?.campaigns || [];
    return {
      totalCampaigns: campaigns.length,
      enabledCampaigns: campaigns.filter((c: Campaign) => c.state === 'ENABLED').length,
      pausedCampaigns: campaigns.filter((c: Campaign) => c.state === 'PAUSED').length,
      archivedCampaigns: campaigns.filter((c: Campaign) => c.state === 'ARCHIVED').length,
      totalBudget: campaigns.reduce((sum: number, c: Campaign) => sum + c.budget.budget, 0)
    };
  }, [campaignsResponse]);

  // Filter campaigns based on search and filters
  const filteredCampaigns = useMemo(() => {
    const campaigns = campaignsResponse?.data?.campaigns || [];
    return campaigns.filter((campaign: Campaign) => {
      const matchesSearch = campaign.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesState = !filters.state || campaign.state === filters.state;
      const matchesTargeting = !filters.targetingType || campaign.targetingType === filters.targetingType;
      
      return matchesSearch && matchesState && matchesTargeting;
    });
  }, [campaignsResponse, filters]);

  if (isLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  const campaigns: Campaign[] = campaignsResponse?.data?.campaigns || [];

  return (
    <>
      <div className="min-h-screen gradient-subtle">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Amazon Campaigns</h1>
            <p className="text-muted-foreground">Manage and monitor your advertising campaigns</p>
          </div>

          {/* Campaign Statistics */}
          <CampaignStatsDisplay stats={campaignStats} />

          {/* Search and Filters */}
          <CampaignSearch filters={filters} onFiltersChange={setFilters} />

          {/* Results Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">
                {filteredCampaigns.length} Campaign{filteredCampaigns.length !== 1 ? 's' : ''}
              </h2>
              {filters.search && (
                <span className="text-sm text-muted-foreground">
                  for "{filters.search}"
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => refetch()}
                className="flex items-center gap-2 border border-input bg-background hover:bg-muted px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Loader2 className="w-4 h-4" />
                Refresh
              </button>
              <button className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                <IndianRupee className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Campaign Grid */}
          {filteredCampaigns.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCampaigns.map((campaign) => (
                <CampaignCard 
                  key={campaign.campaignId} 
                  campaign={campaign} 
                  onEditBudget={setEditingCampaign}
                  onEditHourlyBudget={setEditingHourlyCampaign}
                />
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Archive className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground">
                You don't have any campaigns created yet. Create your first campaign to get started.
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No campaigns found</h3>
              <p className="text-muted-foreground">
                {filters.search ? 
                  `No campaigns match "${filters.search}". Try adjusting your search terms.` :
                  'No campaigns match your current filters. Try adjusting your filters.'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Budget Update Modal */}
      {editingCampaign && (
        <BudgetUpdateModal
          campaign={editingCampaign}
          isOpen={!!editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onUpdate={handleBudgetUpdate}
        />
      )}
      {/* Hourly Budget Modal */}
      {editingHourlyCampaign && (
        <HourlyBudgetModal
          campaign={editingHourlyCampaign}
          isOpen={!!editingHourlyCampaign}
          onClose={() => setEditingHourlyCampaign(null)}
          onUpdate={({ hourlyBudget, active, metadata, campaignId }) => handleHourlyBudgetUpdate({
            campaignId,
            hourlyBudget,
            active,
            metadata,
          })}
        />
      )}
    </>
  );
};
// --- API integration for hourly budget ---
// Add to campaignsApi in api/client.ts:
// updateCampaignHourlyBudget: (campaignId, data) => api.put(`/api/v1/campaigns/${campaignId}/hourly-budget`, data)